import { jsPDF } from "jspdf";
import { jwtDecode } from "jwt-decode"; //  <-- Import jwt-decode

// ---------------------------------------------------------------------
//                               ICONS
// ---------------------------------------------------------------------
export const ArrowIcon = ({ className }) => (
  <svg
    className={className}
    fill="currentColor"
    viewBox="0 0 24 24"
    style={{ width: "24px", height: "24px" }}
  >
    <path d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
  </svg>
);

export const HamburgerIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ width: "32px", height: "32px" }}
  >
    <path d="M3 6H21V8H3V6ZM3 11H21V13H3V11ZM3 16H21V18H3V16Z" />
  </svg>
);

export const DownloadIcon = ({ className }) => (
  <svg
    className={className}
    fill="currentColor"
    viewBox="0 0 24 24"
    style={{ width: "24px", height: "24px" }}
  >
    <path d="M13 3v5.586l1.707-1.707 1.414 1.414L12 12.414l-4.121-4.121 1.414-1.414L11 8.586V3h2zm-7 16h12v2H6v-2z" />
  </svg>
);

export const DownArrowIcon = ({ className }) => (
  <svg
    className={className}
    fill="currentColor"
    viewBox="0 0 24 24"
    style={{ width: "18px", height: "18px" }}
  >
    <path d="M7.41,8.58L12,13.17l4.59-4.59L18,10l-6,6-6-6L7.41,8.58Z" />
  </svg>
);

// Icons for TTS
export const SpeakerIcon = ({ className }) => {
  return (
    <svg
      className={className}
      fill="currentColor"
      height="1em"
      width="1em"
      viewBox="0 0 576 512"
    >
      <path d="M256 88v336c0 13.3-10.7 24-24 24-5.9 0-11.6-2.2-16-6.1L140.2 
      352H56c-13.3 0-24-10.7-24-24V184c0-13.3 10.7-24 24-24h84.2l75.8-69.9c4.4-3.9 
      10.1-6.1 16-6.1 13.3 0 24 10.7 24 24z" />
    </svg>
  );
}

export const PauseIcon = ({ className }) => {
  return (
    <svg
      className={className}
      fill="currentColor"
      height="1em"
      width="1em"
      viewBox="0 0 320 512"
    >
      <path d="M48 64C21.5 64 0 85.5 0 112v288c0 26.5 21.5 
      48 48 48h16c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48H48zm208 
      0c-26.5 0-48 21.5-48 48v288c0 26.5 21.5 48 48 48h16c26.5 0 
      48-21.5 48-48V112c0-26.5-21.5-48-48-48h-16z"/>
    </svg>
  );
}

export const PlayIcon = ({ className }) => {
  return (
    <svg
      className={className}
      fill="currentColor"
      height="1em"
      width="1em"
      viewBox="0 0 384 512"
    >
      <path d="M73 39c-39.5 23-73 64-73 105v224c0 41 33.5 82 73 105 39.6 23 89.3 23 
      128.9 0l160-96c39.6-23 64.1-63 64.1-105s-24.5-82-64.1-105l-160-96c-39.6-23-89.3-23-128.9 
      0z"/>
    </svg>
  );
}

// ---------------------------------------------------------------------
//                       TOKEN EXPIRY HELPERS
// ---------------------------------------------------------------------
export function isTokenExpired(token) {
  if (!token) return true;
  try {
    const decoded = jwtDecode(token);
    // JWT exp is in seconds; Date.now() is in ms => decoded.exp * 1000
    return decoded.exp * 1000 < Date.now();
  } catch (error) {
    return true;
  }
}

// ---------------------------------------------------------------------
//                      PDF DOWNLOAD HELPER
// ---------------------------------------------------------------------
export function handleDownloadPDF(messages, filename = "therapy_transcript") {
  const doc = new jsPDF({
    unit: "pt",
    format: "letter" // 8.5 x 11 inches
  });

  // PAGE LAYOUT CONSTANTS
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // We'll define margins in points (72 pts = 1 inch)
  const marginLeft = 60;
  const marginRight = 60;
  const marginTop = 80;
  const marginBottom = 60;

  // We control vertical writing position with yPos
  let yPos = marginTop;

  // For line spacing
  const lineHeight = 14;
  const paragraphSpacing = 8;

  // Define font sizes
  const titleFontSize = 16;
  const headerFontSize = 14;
  const subheaderFontSize = 12;
  const bodyFontSize = 11;
  const footerFontSize = 10;

  // Define colors
  const titleColor = [20, 20, 90];
  const headerColor = [40, 40, 100];
  const bodyColor = [50, 50, 50];
  const roleColor = [80, 80, 80];

  // This helps us track page numbers
  let currentPage = 1;

  // Generate a string for today's date/time
  const dateStr = new Date().toLocaleString();

  // Is this an analysis result document?
  const isAnalysisResult = filename.includes("psychological-analysis");

  // -------------------------------------------------------------------
  // 1) PAGE HEADER & FOOTER
  // -------------------------------------------------------------------
  function addHeader() {
    // Title
    doc.setFont("Helvetica", "bold");
    doc.setFontSize(titleFontSize);
    doc.setTextColor(titleColor[0], titleColor[1], titleColor[2]);

    const title = isAnalysisResult
      ? "Psychological Analysis Results"
      : "NewTherapy.ai - Session Transcript";

    doc.text(title, marginLeft, 40);

    // Date
    doc.setFontSize(footerFontSize);
    doc.setFont("Helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${dateStr}`, marginLeft, 55);

    // Reset main text color after the header
    doc.setTextColor(bodyColor[0], bodyColor[1], bodyColor[2]);

    // Move yPos to marginTop
    yPos = marginTop;
  }

  function addFooter(pageNum) {
    // Simple page numbering
    doc.setFontSize(footerFontSize);
    doc.setFont("Helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    const pageStr = `Page ${pageNum}`;
    doc.text(pageStr, pageWidth - marginRight, pageHeight - 20, {
      align: "right"
    });

    // Reset color
    doc.setTextColor(bodyColor[0], bodyColor[1], bodyColor[2]);
  }

  // Helper to create a new page
  function addNewPage() {
    doc.addPage();
    currentPage++;
    addHeader();
    addFooter(currentPage);
  }

  // Start with page 1
  addHeader();
  addFooter(currentPage);

  // -------------------------------------------------------------------
  // 2) HANDLE MARKDOWN AND RENDER CONTENT
  // -------------------------------------------------------------------

  // Special markdown handling for analysis results
  if (isAnalysisResult && messages.length === 2 && messages[1].text) {
    doc.setFontSize(bodyFontSize);
    doc.setFont("Helvetica", "normal");

    // Process the markdown content
    const markdownText = messages[1].text;
    const sections = markdownText.split(/(?=^#{1,3} )/m);

    sections.forEach((section, index) => {
      // Skip empty sections
      if (!section.trim()) return;

      // Check if we need a new page
      if (index > 0 && yPos > pageHeight - 200) {
        addNewPage();
      }

      // Process headers (H1, H2, H3)
      if (section.startsWith("# ")) {
        // H1 header
        const headerText = section.split("\n")[0].replace("# ", "");
        doc.setFont("Helvetica", "bold");
        doc.setFontSize(headerFontSize + 2);
        doc.setTextColor(headerColor[0], headerColor[1], headerColor[2]);
        doc.text(headerText, marginLeft, yPos);
        yPos += lineHeight * 1.5;

        // Process the rest of the section (after the header)
        const content = section.replace(/^# .*\n/, "").trim();
        renderMarkdownContent(content);
      }
      else if (section.startsWith("## ")) {
        // H2 header
        const headerText = section.split("\n")[0].replace("## ", "");
        doc.setFont("Helvetica", "bold");
        doc.setFontSize(headerFontSize);
        doc.setTextColor(headerColor[0], headerColor[1], headerColor[2]);
        doc.text(headerText, marginLeft, yPos);
        yPos += lineHeight * 1.3;

        // Process the rest of the section (after the header)
        const content = section.replace(/^## .*\n/, "").trim();
        renderMarkdownContent(content);
      }
      else if (section.startsWith("### ")) {
        // H3 header
        const headerText = section.split("\n")[0].replace("### ", "");
        doc.setFont("Helvetica", "bold");
        doc.setFontSize(subheaderFontSize);
        doc.setTextColor(headerColor[0], headerColor[1], headerColor[2]);
        doc.text(headerText, marginLeft, yPos);
        yPos += lineHeight * 1.2;

        // Process the rest of the section (after the header)
        const content = section.replace(/^### .*\n/, "").trim();
        renderMarkdownContent(content);
      }
      else {
        // Regular paragraph (no header)
        renderMarkdownContent(section);
      }

      // Add space between sections
      yPos += paragraphSpacing * 2;
    });
  }
  // Standard conversation rendering
  else {
    doc.setFontSize(bodyFontSize);

    messages.forEach((msg) => {
      const role = msg.sender === "ai" ? "Therapist" : "Client";
      const messageText = msg.text || "";

      // Distinguish the role in bold
      doc.setFont("Helvetica", "bold");
      doc.setTextColor(roleColor[0], roleColor[1], roleColor[2]);
      doc.text(`${role}:`, marginLeft, yPos, { baseline: "top" });

      // measure the "role" text to offset the actual message
      const roleWidth = doc.getTextWidth(`${role}:`);
      const textStartX = marginLeft + roleWidth + 10;

      // Return to normal font for the message text
      doc.setFont("Helvetica", "normal");
      doc.setTextColor(bodyColor[0], bodyColor[1], bodyColor[2]);

      // We'll split text into lines that fit the width
      const maxTextWidth = pageWidth - marginRight - textStartX;

      // Process any markdown in the message text
      renderMarkdownMessage(messageText, textStartX);

      // Some spacing after each message
      yPos += paragraphSpacing;
    });
  }

  // Helper function to render markdown content with appropriate formatting
  function renderMarkdownContent(content) {
    // Process the content paragraph by paragraph
    const paragraphs = content.split("\n\n");

    paragraphs.forEach((paragraph) => {
      // Skip empty paragraphs
      if (!paragraph.trim()) return;

      // Check if we need a new page
      if (yPos > pageHeight - marginBottom - lineHeight * 3) {
        addNewPage();
      }

      // Handle bullet points
      if (paragraph.match(/^[-*] /m)) {
        const listItems = paragraph.split(/\n(?=[-*] )/);
        listItems.forEach(item => {
          const bulletText = item.replace(/^[-*] /, "").trim();

          // We'll split text into lines that fit the width
          const maxTextWidth = pageWidth - marginRight - marginLeft - 15;
          const lines = doc.splitTextToSize(bulletText, maxTextWidth);

          // Check if we need a new page
          if (yPos + lines.length * lineHeight > pageHeight - marginBottom) {
            addNewPage();
          }

          // Draw bullet
          doc.setFont("Helvetica", "bold");
          doc.text("•", marginLeft, yPos);

          // Draw text
          doc.setFont("Helvetica", "normal");
          doc.setTextColor(bodyColor[0], bodyColor[1], bodyColor[2]);

          lines.forEach((line, idx) => {
            doc.text(line, marginLeft + 15, yPos + idx * lineHeight);
          });

          yPos += lines.length * lineHeight + paragraphSpacing;
        });
      }
      // Handle bold text with ** or __
      else if (paragraph.includes("**") || paragraph.includes("__")) {
        // We'll process this paragraph with mixed formatting
        processMixedFormatting(paragraph);
      }
      // Regular paragraph
      else {
        // We'll split text into lines that fit the width
        const maxTextWidth = pageWidth - marginRight - marginLeft;
        const lines = doc.splitTextToSize(paragraph, maxTextWidth);

        // Check if we need a new page
        if (yPos + lines.length * lineHeight > pageHeight - marginBottom) {
          addNewPage();
        }

        // Draw text
        doc.setFont("Helvetica", "normal");
        doc.setTextColor(bodyColor[0], bodyColor[1], bodyColor[2]);

        lines.forEach((line, idx) => {
          doc.text(line, marginLeft, yPos + idx * lineHeight);
        });

        yPos += lines.length * lineHeight + paragraphSpacing;
      }
    });
  }

  // Helper function to render markdown message with appropriate formatting
  function renderMarkdownMessage(messageText, startX) {
    // We'll split text into lines that fit the width
    const maxTextWidth = pageWidth - marginRight - startX;
    const lines = doc.splitTextToSize(messageText, maxTextWidth);

    // Check if we have enough space for all lines on this page
    const neededSpace = lines.length * lineHeight + paragraphSpacing;
    if (yPos + neededSpace > pageHeight - marginBottom) {
      // Not enough space, create a new page
      addNewPage();
    }

    // Draw text
    lines.forEach((line, index) => {
      const lineYPos = yPos + index * lineHeight;
      doc.text(line, startX, lineYPos, { baseline: "top" });
    });

    yPos += neededSpace;
  }

  // Helper to process text with mixed formatting (bold, etc.)
  function processMixedFormatting(text) {
    // TODO: For a more sophisticated implementation, we would
    // parse the text and handle bold, italic, etc. inline
    // For now, we'll just render the text as is

    // We'll split text into lines that fit the width
    const maxTextWidth = pageWidth - marginRight - marginLeft;
    const lines = doc.splitTextToSize(text, maxTextWidth);

    // Check if we need a new page
    if (yPos + lines.length * lineHeight > pageHeight - marginBottom) {
      addNewPage();
    }

    // Draw text
    doc.setFont("Helvetica", "normal");
    doc.setTextColor(bodyColor[0], bodyColor[1], bodyColor[2]);

    lines.forEach((line, idx) => {
      doc.text(line, marginLeft, yPos + idx * lineHeight);
    });

    yPos += lines.length * lineHeight + paragraphSpacing;
  }

  // -------------------------------------------------------------------
  // 3) SAVE PDF
  // -------------------------------------------------------------------
  doc.save(`${filename}.pdf`);
}

// ---------------------------------------------------------------------
//                  MICROPHONE / RECORDING HELPERS
// ---------------------------------------------------------------------

/**
 * Starts the recording by:
 * 1) Clearing audio chunks
 * 2) Getting mic stream
 * 3) Creating a MediaRecorder and starting
 * 4) Setting a timeout to auto-stop
 */
export async function startRecording({
  audioChunksRef,
  streamRef,
  mediaRecorderRef,
  setRecording,
  recordingTimeoutRef,
  MAX_RECORDING_TIME,
  handleRecordingStop,
}) {
  audioChunksRef.current = [];
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  streamRef.current = stream;

  mediaRecorderRef.current = new MediaRecorder(stream);
  mediaRecorderRef.current.ondataavailable = (event) => {
    if (event.data.size > 0) {
      audioChunksRef.current.push(event.data);
    }
  };
  mediaRecorderRef.current.onstop = () =>
    handleRecordingStop({
      audioChunksRef,
    });

  mediaRecorderRef.current.start();
  setRecording(true);

  // auto-stop
  recordingTimeoutRef.current = setTimeout(() => {
    stopRecording({
      mediaRecorderRef,
      streamRef,
      setRecording,
      recordingTimeoutRef,
    });
  }, MAX_RECORDING_TIME * 1000);
}

/**
 * Stops the recording if active, stops all mic tracks,
 * and clears the auto-stop timer.
 */
export function stopRecording({
  mediaRecorderRef,
  streamRef,
  setRecording,
  recordingTimeoutRef,
}) {
  if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
    mediaRecorderRef.current.stop();
  }
  if (streamRef.current) {
    streamRef.current.getTracks().forEach((track) => track.stop());
  }
  setRecording(false);
  if (recordingTimeoutRef.current) {
    clearTimeout(recordingTimeoutRef.current);
  }
}

/**
 * Once recording stops, transcribe the audio:
 * 1) Create a Blob
 * 2) Send to server
 * 3) Update the input text or call voice note callback
 */
export async function handleRecordingStop({
  audioChunksRef,
  setIsTranscribing,
  setInputText,
  onVoiceNoteFinished,
}) {
  if (setIsTranscribing) setIsTranscribing(true);
  const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
  const formData = new FormData();
  formData.append("file", audioBlob, "speech.webm");

  try {
    const response = await fetch("/api/transcribe", {
      method: "POST",
      body: formData,
    });
    const data = await response.json();
    const transcription = data.transcription;

    if (onVoiceNoteFinished) {
      // If we're in voice note mode, use the callback
      onVoiceNoteFinished(transcription);
    } else if (setInputText) {
      // Otherwise update the input text field
      setInputText((prev) => (prev ? prev + " " + transcription : transcription));
    }
  } catch (error) {
    console.error("Error sending audio to server:", error);
    alert("Error transcribing audio. Please try again.");
  } finally {
    if (setIsTranscribing) setIsTranscribing(false);
  }
}

/**
 * If user clicks mic button:
 * 1) If already recording => stop
 * 2) Else => start
 */
export async function handleMicClick({
  recording,
  startRecordingFn,
  stopRecordingFn,
}) {
  if (recording) {
    stopRecordingFn();
  } else {
    await startRecordingFn();
  }
}